import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/ui/header';
import { Footer } from '../components/ui/footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';
import { 
  GraduationCap, 
  Award, 
  Users, 
  BookOpen, 
  CheckCircle, 
  Star, 
  Clock, 
  MapPin,
  ExternalLink,
  Play,
  Scissors,
  Heart,
  Download,
  Mail,
  User,
  Phone,
  Loader2,
  Gift
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const TrainingAcademy = () => {
  const navigate = useNavigate();
  const [reportForm, setReportForm] = useState({
    full_name: '',
    email: '',
    whatsapp: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/report-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reportForm)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSubmitted(true);
        toast.success('Report sent!', {
          description: 'Check your email for the download link.'
        });
      } else {
        toast.error('Something went wrong', {
          description: 'Please try again.'
        });
      }
    } catch (error) {
      toast.error('Connection error', {
        description: 'Please check your internet and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const courses = [
    {
      title: "Hair Extensions Mastery",
      description: "Learn 18+ professional hair extension techniques from tape-ins to micro rings, nano tips, and fusion methods.",
      duration: "5 Days Intensive",
      level: "Beginner to Advanced",
      includes: ["Hands-on practice", "Starter kit included", "Certificate of completion", "Ongoing support"],
      image: "https://customer-assets.emergentagent.com/job_franchise-cta-fix/artifacts/ayhsds4r_Hair%20extensions%20training%20coursestraining%20courses_20260109_152647_0001.png",
      link: "https://www.hairextensionstraining.academy"
    },
    {
      title: "Hook & Latch Entrepreneur Bootcamp",
      description: "Intensive 7-day bootcamp covering 18 hair extensions methods. Start your own hair extensions business with comprehensive training and business setup support.",
      duration: "7 Days Intensive",
      level: "Beginner to Advanced",
      includes: ["18 Hair Extensions Methods", "Business Setup Guidance", "Marketing Materials", "Certificate & Portfolio"],
      image: "https://customer-assets.emergentagent.com/job_franchise-cta-fix/artifacts/f0pf2b1x_Hair%20extensions%20training%20coursestraining%20courses_20260109_152647_0000.png",
      link: "https://www.virtualspeedyservices.uk/courses/hook-and-latch-entrepreneur-hair-extensions-course"
    },
    {
      title: "Wig Making & Customisation",
      description: "Master the art of creating bespoke handmade wigs, from measuring and fitting to ventilation and styling.",
      duration: "4 Days Intensive",
      level: "Advanced",
      includes: ["Lace front techniques", "Custom colouring", "Fitting & adjustment", "Business pricing"],
      image: "https://customer-assets.emergentagent.com/job_franchise-cta-fix/artifacts/jfdjbpu9_Hair%20extensions%20training%20coursestraining%20courses_20260109_152647_0002.png",
      link: "https://www.hairextensionstraining.academy"
    }
  ];

  const stats = [
    { number: "3,900+", label: "Students Trained", icon: Users },
    { number: "25+", label: "Years Experience", icon: Award },
    { number: "18+", label: "Techniques Taught", icon: Scissors },
    { number: "98%", label: "Success Rate", icon: Star }
  ];

  const testimonials = [
    {
      name: "Emma Thompson",
      role: "Salon Owner, Manchester",
      quote: "The training completely transformed my business. I now offer services I never thought possible and my client retention has doubled.",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop"
    },
    {
      name: "Sarah Williams",
      role: "Hair Extension Specialist, London",
      quote: "Diane's expertise is unmatched. The hands-on training gave me confidence to work with even the most challenging hair types.",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop"
    },
    {
      name: "Michelle Davis",
      role: "Hair Loss Consultant, Birmingham",
      quote: "This training opened up a whole new revenue stream for my salon. The hair loss module alone was worth the investment.",
      image: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <SEO 
        page="trainingAcademy"
        title="Hair Extensions Training Academy | Need A Hair Makeover"
        description="Professional hair extensions and hair loss specialist training courses. Learn 18+ techniques from industry experts with 25+ years experience. Accredited certification included."
      />
      <Header onApplyClick={() => navigate('/#apply-form')} />

      {/* Header Banner Image */}
      <div className="pt-20">
        <div className="w-full">
          <img 
            src="https://customer-assets-lqy194kg.emergentagent.net/job_f194cab7-0920-47a1-bac9-625d43d4e059/artifacts/0g4l1dr6_4%20mistakes%20to%20avoid%20before%20starting%20hair%20business.webp"
            alt="STOP! Read This Before You Spend Thousands Starting a Hair Business - Four costly mistakes that could leave your money trapped in stock, an empty salon and a business that never grows. By Diane Shawe MEd"
            className="w-full h-auto object-cover"
          />
        </div>
      </div>

      {/* Featured Video Section */}
      <section className="py-12 bg-dark">
        <div className="max-w-5xl mx-auto px-6">
          <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl border-4 border-accent-primary/20">
            <iframe
              src="https://www.youtube.com/embed/pHAx8rQMKNU"
              title="Hair Business Training Video"
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      </section>

      {/* Free Report Lead Capture Form */}
      <section className="py-16 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          {!submitted ? (
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
              <div className="grid md:grid-cols-2">
                {/* Left side - Benefits */}
                <div className="bg-gradient-to-br from-gray-900 to-gray-800 p-8 md:p-10 text-white">
                  <div className="inline-flex items-center gap-2 bg-amber-500/20 px-4 py-2 rounded-full mb-6">
                    <Gift className="w-5 h-5 text-amber-400" />
                    <span className="text-amber-400 font-semibold text-sm">FREE REPORT</span>
                  </div>
                  
                  <h2 className="text-2xl md:text-3xl font-bold mb-4">
                    Claim Your Free Report Now!
                  </h2>
                  
                  <p className="text-gray-300 mb-6">
                    Discover the <strong className="text-amber-400">4 costly mistakes</strong> that could leave your money trapped in stock, an empty salon, and a business that never grows.
                  </p>
                  
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent-primary flex-shrink-0 mt-0.5" />
                      <span>Learn what NOT to do before spending thousands</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent-primary flex-shrink-0 mt-0.5" />
                      <span>Avoid the traps that catch 90% of new salon owners</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent-primary flex-shrink-0 mt-0.5" />
                      <span>Save time, money and heartache</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent-primary flex-shrink-0 mt-0.5" />
                      <span>Written by Diane Shawe MEd - 25+ years experience</span>
                    </li>
                  </ul>
                  
                  <div className="mt-8 pt-6 border-t border-gray-700">
                    <p className="text-sm text-gray-400">
                      Join <strong className="text-white">3,900+</strong> professionals who have transformed their careers with our guidance.
                    </p>
                  </div>
                </div>
                
                {/* Right side - Form */}
                <div className="p-8 md:p-10">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Get Instant Access
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Enter your details below and we will send the report straight to your inbox.
                  </p>
                  
                  <form onSubmit={handleReportSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <User className="w-4 h-4 inline mr-2" />
                        Full Name
                      </label>
                      <Input
                        type="text"
                        placeholder="Enter your full name"
                        value={reportForm.full_name}
                        onChange={(e) => setReportForm({...reportForm, full_name: e.target.value})}
                        required
                        disabled={isSubmitting}
                        data-testid="report-name-input"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-0"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <Mail className="w-4 h-4 inline mr-2" />
                        Email Address
                      </label>
                      <Input
                        type="email"
                        placeholder="Enter your email"
                        value={reportForm.email}
                        onChange={(e) => setReportForm({...reportForm, email: e.target.value})}
                        required
                        disabled={isSubmitting}
                        data-testid="report-email-input"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-0"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <Phone className="w-4 h-4 inline mr-2" />
                        WhatsApp Number
                      </label>
                      <Input
                        type="tel"
                        placeholder="Enter your WhatsApp number"
                        value={reportForm.whatsapp}
                        onChange={(e) => setReportForm({...reportForm, whatsapp: e.target.value})}
                        required
                        disabled={isSubmitting}
                        data-testid="report-whatsapp-input"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-0"
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="report-submit-btn"
                      className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-4 rounded-xl text-lg font-bold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Download className="w-5 h-5 mr-2" />
                          Send Me The Free Report
                        </>
                      )}
                    </Button>
                    
                    <p className="text-xs text-gray-500 text-center mt-4">
                      🔒 We respect your privacy. Your details are safe and will never be shared.
                    </p>
                  </form>
                </div>
              </div>
            </div>
          ) : (
            /* Success State */
            <div className="bg-white rounded-3xl shadow-2xl p-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Check Your Email!
              </h2>
              <p className="text-lg text-gray-600 mb-6 max-w-md mx-auto">
                We have sent your free report to <strong>{reportForm.email}</strong>. 
                If you do not see it, please check your spam folder.
              </p>
              <Button
                onClick={() => {
                  setSubmitted(false);
                  setReportForm({ full_name: '', email: '', whatsapp: '' });
                }}
                variant="outline"
                className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Request Another Copy
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-dark via-dark to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-accent-primary rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-gold rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-accent-primary/20 px-4 py-2 rounded-full mb-6">
              <GraduationCap className="w-5 h-5 text-accent-primary" />
              <span className="text-accent-primary font-semibold">Hair Extensions Training Academy</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Transform Your Career with<br />
              <span className="text-accent-primary">Expert Hair Training</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Join over 3,900 professionals who have mastered hair extensions and hair loss solutions 
              with our industry-leading training programmes. Led by Diane Shawe with 25+ years of expertise.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-accent-primary hover:bg-accent-primary/90 text-lg px-8 py-6"
                style={{ color: 'white' }}
                onClick={() => window.open('https://www.hairextensionstraining.academy', '_blank')}
              >
                <ExternalLink className="w-5 h-5 mr-2" />
                Visit Training Academy
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-dark text-lg px-8 py-6"
                onClick={() => window.open('https://www.youtube.com/@needahairmakeover', '_blank')}
              >
                <Play className="w-5 h-5 mr-2" />
                Watch Training Videos
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                <stat.icon className="w-8 h-8 text-accent-primary mx-auto mb-3" />
                <div className="text-3xl font-bold text-white mb-1">{stat.number}</div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4">Our Training Courses</h2>
            <p className="text-lg text-subtle max-w-2xl mx-auto">
              Comprehensive programmes designed to take you from beginner to expert, 
              with hands-on training and ongoing support.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {courses.map((course, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow flex flex-col">
                <div className="h-48 overflow-hidden">
                  <img 
                    src={course.image} 
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="text-xl font-bold text-dark mb-2">{course.title}</h3>
                  <p className="text-subtle mb-4">{course.description}</p>
                  
                  <div className="flex items-center gap-4 mb-4 text-sm">
                    <div className="flex items-center gap-1 text-accent-primary">
                      <Clock className="w-4 h-4" />
                      {course.duration}
                    </div>
                    <div className="flex items-center gap-1 text-gray-600">
                      <BookOpen className="w-4 h-4" />
                      {course.level}
                    </div>
                  </div>

                  <div className="border-t pt-4 flex-1">
                    <p className="text-sm font-semibold text-dark mb-2">What&apos;s Included:</p>
                    <ul className="space-y-1">
                      {course.includes.map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-subtle">
                          <CheckCircle className="w-4 h-4 text-accent-primary flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <Button
                    className="mt-4 w-full bg-accent-primary hover:bg-accent-primary/90"
                    style={{ color: 'white' }}
                    onClick={() => window.open(course.link, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Learn More & Register
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              size="lg"
              className="bg-accent-primary hover:bg-accent-primary/90 px-8 py-6"
              style={{ color: 'white' }}
              onClick={() => window.open('https://www.hairextensionstraining.academy', '_blank')}
            >
              View All Courses & Book Now
              <ExternalLink className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Video Section */}
      <section className="py-20 bg-dark">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">See Our Training in Action</h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Watch how our expert-led training transforms careers and empowers professionals 
              to deliver exceptional results.
            </p>
          </div>
          
          <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video">
            <iframe
              src="https://www.youtube.com/embed/BNcyfQPphGU?rel=0"
              title="Hair Extensions Training Academy"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            ></iframe>
          </div>
          
          <div className="text-center mt-8">
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-dark"
              onClick={() => window.open('https://www.youtube.com/@needahairmakeover', '_blank')}
            >
              <Play className="w-5 h-5 mr-2" />
              View More Videos on YouTube
            </Button>
          </div>
        </div>
      </section>

      {/* Why Train With Us */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6">
                Why Train With Us?
              </h2>
              <p className="text-lg text-subtle mb-8">
                Our academy is led by Diane Shawe, an award-winning hair extension specialist 
                with over 25 years of industry experience. We've trained professionals who now 
                work with celebrities, TV productions, and high-end salons across the UK.
              </p>

              <div className="space-y-4">
                {[
                  "Small class sizes for personalised attention",
                  "Real client practice during training",
                  "Industry-recognised certification",
                  "Ongoing mentorship and support",
                  "Access to exclusive supplier discounts",
                  "Business building and marketing guidance"
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-accent-primary rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-dark">{benefit}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-6 bg-white rounded-xl shadow-lg">
                <div className="flex items-center gap-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_hair-opportunity/artifacts/founder_diane_shawe.jpg"
                    alt="Diane Shawe - Founder"
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-bold text-dark">Diane Shawe</p>
                    <p className="text-sm text-subtle">Founder & Lead Trainer</p>
                    <p className="text-sm text-accent-primary">30+ Industry Awards</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_hair-opportunity/artifacts/salon_interior_2.png"
                alt="Training Academy"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-accent-primary text-white p-6 rounded-xl shadow-lg">
                <div className="text-3xl font-bold">98%</div>
                <div className="text-sm">of graduates recommend us</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Course Quiz Assessment Banner */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <a 
            href="https://www.qualiquiz.app/quiz/which-hair-extension-course-is-right-for-you-take-60-second-assessment"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full cursor-pointer hover:opacity-95 transition-opacity hover:shadow-xl"
            data-testid="quiz-banner-training"
          >
            <img 
              src="https://customer-assets-lqy194kg.emergentagent.net/job_f194cab7-0920-47a1-bac9-625d43d4e059/artifacts/zgn1lc97_AISelect_20260821_073238_Chrome.jpg"
              alt="Which Hair Extension Course Is Right for You? 60-Second Assessment - Understand whether you should start, specialise, or scale into a business"
              className="w-full h-auto rounded-2xl shadow-lg"
            />
          </a>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4">What Our Graduates Say</h2>
            <p className="text-lg text-subtle">Success stories from professionals who trained with us</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 relative">
                <div className="absolute -top-4 left-6 text-6xl text-accent-primary/20 font-serif">"</div>
                <p className="text-subtle mb-6 relative z-10">{testimonial.quote}</p>
                <div className="flex items-center gap-3">
                  <img 
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-dark">{testimonial.name}</p>
                    <p className="text-sm text-subtle">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-accent-primary to-green-700">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Heart className="w-12 h-12 text-white/80 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Transform Your Career?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Whether you're starting fresh or expanding your skills, our training programmes 
            will give you the expertise and confidence to succeed.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-accent-primary hover:bg-gray-100 text-lg px-8 py-6"
              onClick={() => window.open('https://www.hairextensionstraining.academy', '_blank')}
            >
              <GraduationCap className="w-5 h-5 mr-2" />
              Enrol Now
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10 text-lg px-8 py-6"
              onClick={() => navigate('/hair-extensions-franchise-opportunity')}
            >
              Learn About Franchise
            </Button>
          </div>
          <p className="text-white/70 mt-6 text-sm">
            Or call us: <a href="tel:+443300430703" className="underline">0330 043 0703</a>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TrainingAcademy;
